// src/pages/Admin/Settings/SettingsPage.jsx
import React, { useMemo, useState } from "react";
import {
  FaUserFriends,
  FaBuilding,
  FaHome,
  FaFileInvoice,
  FaHandshake,
  FaChartBar,
  FaCogs,
} from "react-icons/fa";
import { ToastContainer } from "react-toastify";

import useScrollToTop from "../../hooks/useScrollToTop";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";

// Tes composants existants
import SettingsForm from "./SettingsForm";
import HomeSettingsForm from "./HomeSettingsForm";
import PaiementSettingsForm from "./PaiementSettingsForm";
import TeamManagement from "./TeamManagement";
import SettingsPartenaires from "./SettingsPartenaires";

// Nouveau composant (fourni plus bas)
import VisitesDashboard from "./components/VisitesDashboard";
import SettingsSidebar from "./components/SettingsSidebar";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

export default function SettingsPage() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Première page = visites
  const [active, setActive] = useState("visites");

  const NAV = useMemo(
    () => [
      { key: "visites", label: "Visites & stats", icon: FaChartBar },
      { key: "equipes", label: "Équipe", icon: FaUserFriends },
      { key: "comite", label: "Paramètres Comité", icon: FaBuilding },
      { key: "pageAccueil", label: "Page Accueil", icon: FaHome },
      { key: "partenaires", label: "Partenaires", icon: FaHandshake },
    ],
    [],
  );

  const renderContent = () => {
    switch (active) {
      case "visites":
        return <VisitesDashboard />;
      case "equipes":
        return <TeamManagement />;
      case "comite":
        return <SettingsForm />;
      case "pageAccueil":
        return <HomeSettingsForm />;

      case "partenaires":
        return <SettingsPartenaires />;
      default:
        return <VisitesDashboard />;
    }
  };

  const activeItem = NAV.find((n) => n.key === active);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Espace top pour ta topbar/header existante */}
      <div className="pt-[120px] sm:pt-[130px] md:pt-[150px]" />

      <div className="mx-auto w-full max-w-[1400px] px-3 sm:px-4">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200/70 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 backdrop-blur shadow-sm">
          {/* décor subtil */}
          <div className="pointer-events-none absolute -top-24 -left-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />

          <div className="relative grid grid-cols-1 lg:grid-cols-[320px_1fr]">
            {/* Sidebar */}
            <SettingsSidebar
              brandTitle="Paramètres"
              brandSubtitle="Comité de Gironde"
              icon={FaCogs}
              items={NAV}
              activeKey={active}
              onChange={setActive}
            />

            {/* Main */}
            <main className="min-w-0">
              {/* Header */}
              <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-slate-200/70 dark:border-slate-800">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
                      Espace Admin
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <div className="text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white">
                        Dashboard paramètres
                      </div>
                      {userRole && (
                        <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-900 text-white dark:bg-white dark:text-slate-900">
                          {userRole}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <span className="truncate">
                        {activeItem?.label || "Visites & stats"}
                      </span>
                    </div>
                  </div>

                  {/* Nav mobile (fallback) */}
                  <div className="lg:hidden w-full">
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                      {NAV.map((it) => {
                        const Icon = it.icon;
                        const is = active === it.key;
                        return (
                          <button
                            key={it.key}
                            onClick={() => setActive(it.key)}
                            className={cn(
                              "shrink-0 inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold ring-1 transition",
                              is
                                ? "bg-slate-900 text-white ring-slate-900 dark:bg-white dark:text-slate-900 dark:ring-white"
                                : "bg-white/70 dark:bg-slate-900/50 text-slate-700 dark:text-slate-200 ring-slate-200/70 dark:ring-slate-800 hover:bg-white dark:hover:bg-slate-900",
                            )}
                          >
                            <Icon />
                            {it.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-4 sm:px-6 py-5 sm:py-6">
                <div className="mx-auto w-full max-w-6xl">
                  {renderContent()}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
